/**
 * ManoPaieskos Component (Employer Saved Searches)
 * 
 * Allows employers to save and manage candidate search criteria.
 * Similar to SavedSearches.tsx but for employer candidate searches.
 * 
 * @route /mano-paieskos
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Settings, Trash2, Search as SearchIcon, ChevronRight, Plus,
         MapPin, Briefcase, Globe, GraduationCap, Monitor, Car, Clock, Euro, User, X } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import FilterModal from '../components/Jobs/FilterModal';
import { LITHUANIAN_CITIES } from '../constants/cities';
import { FilterOptions, filtersToQueryParams } from './Skelbimai';
import './ManoPaieskos.scss';

interface EmployerSavedSearch {
  id: number;
  title: string;
  filters_json?: string;
  // Search keywords
  keywords?: string[];
  // Personal Data
  location?: string[];
  age_range?: string;
  gender?: string;
  // Work Experience
  experience_years?: string;
  previous_companies?: string;
  positions?: string[];
  // Education
  education_level?: string[];
  field_of_study?: string[];
  // Skills
  technical_skills?: string[];
  languages?: string[];
  // Requirements
  desired_salary?: string;
  desired_work_type?: string[];
  desired_location?: string[];
  
  is_subscribed: boolean;
  total_results: number;
  new_results: number;
  created_at: string;
  updated_at: string;
}

const EMPTY_FILTERS: FilterOptions = {
  searchKeyword: '',
  keywords: [],
  location: [],
  experience: [],
  education: [],
  languages: [],
  computerSkills: [],
  drivingLicense: [],
  salary: '',
  workType: [],
  age: '',
  gender: [],
  status: [],
  activity: '',
  cvLanguage: [],
  hasPhoto: false,
  hasRecommendation: false,
  companyNames: [],
  lastWorkField: [],
  companyActivity: [],
};

const savedSearchToFilterOptions = (search: EmployerSavedSearch): FilterOptions => {
  if (search.filters_json) {
    try { return JSON.parse(search.filters_json); } catch (_) {}
  }
  // Legacy fallback — best-effort mapping from old flat fields
  return {
    ...EMPTY_FILTERS,
    keywords: search.keywords || search.positions || [],
    location: search.location || [],
    age: search.age_range || '',
    gender: search.gender ? [search.gender] : [],
    education: search.education_level || [],
    languages: search.languages || [],
    computerSkills: search.technical_skills || [],
    salary: search.desired_salary || '',
    workType: search.desired_work_type || [],
    companyNames: search.previous_companies ? [search.previous_companies] : [],
  };
};

interface CVTemplate { id: number; category: string; value: string; label?: string; parent_value?: string | null; }

const ManoPaieskos: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState<EmployerSavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [allCVs, setAllCVs] = useState<any[]>([]);
  const [loadingCVs, setLoadingCVs] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSearch, setEditingSearch] = useState<EmployerSavedSearch | null>(null);
  const [selectedSearches, setSelectedSearches] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  // CV templates for filter options
  const [cvTemplates, setCvTemplates] = useState<{
    language?: CVTemplate[]; education_level?: CVTemplate[]; work_field?: CVTemplate[];
    work_type?: CVTemplate[]; driving_category?: CVTemplate[];
    computer_skill_office?: CVTemplate[]; computer_skill_programming?: CVTemplate[];
    computer_skill_design?: CVTemplate[]; computer_skill_os?: CVTemplate[];
    computer_skill_accounting?: CVTemplate[]; computer_skill_database?: CVTemplate[];
    city?: CVTemplate[];
  }>({});

  // Modal state — individual filter fields
  const [modalTitle, setModalTitle] = useState('');
  const [formKeywords, setFormKeywords] = useState<string[]>([]);
  const [formLocation, setFormLocation] = useState<string[]>([]);
  const [formGender, setFormGender] = useState<string[]>([]);
  const [formAge, setFormAge] = useState('');
  const [formEducation, setFormEducation] = useState<string[]>([]);
  const [formLanguages, setFormLanguages] = useState<string[]>([]);
  const [formComputerSkills, setFormComputerSkills] = useState<string[]>([]);
  const [formDrivingLicense, setFormDrivingLicense] = useState<string[]>([]);
  const [formWorkFields, setFormWorkFields] = useState<string[]>([]);
  const [formSalary, setFormSalary] = useState('');
  const [formSalarySlider, setFormSalarySlider] = useState(0);
  const [formWorkType, setFormWorkType] = useState<string[]>([]);
  const [formSubscribed, setFormSubscribed] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeFilterPanel, setActiveFilterPanel] = useState<string | null>(null);
  // search inputs for sub-modals
  const [locationSearch, setLocationSearch] = useState('');
  const [keywordInputVal, setKeywordInputVal] = useState('');

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      fetchSavedSearches();
      fetchAllCVs();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    fetch(`${API_URL}/cv-templates?grouped=true`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setCvTemplates(d.data); })
      .catch(() => {});
  }, []);


  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSavedSearches(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCVs = async () => {
    try {
      setLoadingCVs(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/cv/employer/visible`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAllCVs(result.data || result || []);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    } finally {
      setLoadingCVs(false);
    }
  };

  const countMatchingCVs = (search: EmployerSavedSearch): number => {
    return allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }

      // Add more filters as needed
      return true;
    }).length;
  };

  const countNewCVs = (search: EmployerSavedSearch): number => {
    const matchingCVs = allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }
      return true;
    });

    // Count CVs created in last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return matchingCVs.filter(cv => {
      if (cv.created_at) {
        const cvCreatedDate = new Date(cv.created_at);
        return cvCreatedDate >= sevenDaysAgo;
      }
      return false;
    }).length;
  };

  const toggleArr = (arr: string[], val: string) =>
    arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  const openModal = (search: EmployerSavedSearch | null) => {
    setEditingSearch(search);
    const f = search ? savedSearchToFilterOptions(search) : EMPTY_FILTERS;
    setModalTitle(search ? search.title : '');
    setFormKeywords(f.keywords);
    setFormLocation(f.location);
    setFormGender(f.gender);
    setFormAge(f.age || '');
    setFormEducation(f.education);
    setFormLanguages(f.languages);
    setFormComputerSkills(f.computerSkills);
    setFormDrivingLicense(f.drivingLicense);
    setFormWorkFields(f.experience);
    setFormSalary(f.salary || '');
    setFormSalarySlider(f.salary ? parseInt(f.salary) || 0 : 0);
    setFormWorkType(f.workType);
    setFormSubscribed(search ? search.is_subscribed : true);
    setActiveFilterPanel(null);
    setLocationSearch('');
    setKeywordInputVal('');
    setShowCreateModal(true);
  };

  const handleCreateSearch = () => openModal(null);
  const handleEditSearch = (search: EmployerSavedSearch) => openModal(search);

  const handleSaveSearch = async () => {
    if (!modalTitle.trim()) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const f: FilterOptions = {
        ...EMPTY_FILTERS,
        keywords: formKeywords,
        location: formLocation,
        gender: formGender,
        age: formAge,
        education: formEducation,
        languages: formLanguages,
        computerSkills: formComputerSkills,
        drivingLicense: formDrivingLicense,
        experience: formWorkFields,
        salary: formSalary,
        workType: formWorkType,
      };

      const payload = {
        title: modalTitle.trim(),
        filters_json: JSON.stringify(f),
        keywords: formKeywords.length > 0 ? formKeywords : null,
        location: formLocation.length > 0 ? formLocation : null,
        age_range: formAge || null,
        gender: formGender.length > 0 ? formGender[0] : null,
        education_level: formEducation.length > 0 ? formEducation : null,
        technical_skills: formComputerSkills.length > 0 ? formComputerSkills : null,
        languages: formLanguages.length > 0 ? formLanguages : null,
        desired_salary: formSalary || null,
        desired_work_type: formWorkType.length > 0 ? formWorkType : null,
        positions: formKeywords.length > 0 ? formKeywords : null,
        is_subscribed: formSubscribed,
      };

      const url = editingSearch
        ? `${API_URL}/employer-saved-searches/${editingSearch.id}`
        : `${API_URL}/employer-saved-searches`;

      const response = await fetch(url, {
        method: editingSearch ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCreateModal(false);
        fetchSavedSearches();
        showToast(editingSearch ? 'Paieška atnaujinta' : 'Paieška išsaugota', 'success');
      } else {
        showToast('Klaida išsaugant paiešką', 'error');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      showToast('Klaida išsaugant paiešką', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSearch = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const handleGoToSearch = (search: EmployerSavedSearch) => {
    const filters = savedSearchToFilterOptions(search);
    const params = filtersToQueryParams(filters);
    params.set('mode', 'employer');
    navigate(`/skelbimai?${params.toString()}`);
  };


  const formatCriteria = (search: EmployerSavedSearch): string => {
    const criteria: string[] = [];

    if (search.keywords && search.keywords.length > 0) {
      criteria.push(`Raktažodžiai: ${search.keywords.join(', ')}`);
    }
    if (search.positions && search.positions.length > 0) {
      criteria.push(`Pareigos: ${search.positions.join(', ')}`);
    }
    if (search.gender) {
      criteria.push(search.gender === 'male' ? 'Vyras' : 'Moteris');
    }
    if (search.age_range) {
      criteria.push(`${search.age_range} metų`);
    }
    if (search.experience_years) {
      criteria.push(`Patirtis: ${search.experience_years}`);
    }
    if (search.location && search.location.length > 0) {
      criteria.push(search.location.join(', '));
    }

    return criteria.length > 0 ? criteria.join(', ') : 'Visi kriterijai';
  };

  const handleToggleSelectAll = () => {
    const filteredSearches = filterSearchesByTitle(savedSearches);
    
    if (selectedSearches.length === filteredSearches.length && filteredSearches.length > 0) {
      setSelectedSearches([]);
    } else {
      setSelectedSearches(filteredSearches.map(s => s.id));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedSearches(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedSearches.length === 0) return;

    if (!await confirm(`Ar tikrai norite ištrinti ${selectedSearches.length} paieškų? Šio veiksmo negalima atšaukti.`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      await Promise.all(
        selectedSearches.map(id =>
          fetch(`${API_URL}/employer-saved-searches/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        )
      );

      setSelectedSearches([]);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting searches:', error);
      showToast('Įvyko klaida trinant paieškas', 'error');
    }
  };

  const filterSearchesByTitle = (searches: EmployerSavedSearch[]) => {
    if (!searchTitleFilter.trim()) return searches;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return searches.filter(search => 
      search.title.toLowerCase().includes(filterLower)
    );
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="mano-paieskos-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employer
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mano-paieskos-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Mano paieškos</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedSearches.length > 0 && selectedSearches.length === filterSearchesByTitle(savedSearches).length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedSearches.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <SearchIcon size={16} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={handleCreateSearch}>
              <Plus size={16} />
              <span>Nauja paieška</span>
            </button>
          </div>
        </div>

        <div className="saved-section">
          {loading || loadingCVs ? (
            <div className="loading">Kraunama...</div>
          ) : savedSearches.length === 0 ? (
            <div className="empty-state">
              <SearchIcon size={48} />
              <p>Dar neturite išsaugotų paieškų</p>
              <button className="btn-primary" onClick={handleCreateSearch}>
                Sukurti pirmą paiešką
              </button>
            </div>
          ) : (
            <div className="searches-grid">
                  {filterSearchesByTitle(savedSearches).map((search) => (
                    <div key={search.id} className={`search-card ${selectedSearches.includes(search.id) ? 'selected' : ''}`}>
                      <div className="search-card-header">
                        <div className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={selectedSearches.includes(search.id)}
                            onChange={() => handleToggleSelect(search.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="header-actions">
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSearch(search);
                            }}
                            title="Nustatymai"
                          >
                            <Settings size={18} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSearch(search.id);
                            }}
                            title="Ištrinti"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="search-card-body" onClick={() => handleGoToSearch(search)}>
                        <h3 className="search-title">{search.title}</h3>
                        <p className="search-criteria">
                          <span className="criteria-label">Kriterijai:</span><br />
                          {formatCriteria(search)}
                        </p>
                      </div>

                      <div className="search-card-footer">
                        <span className="result-count">
                          Iš viso: <strong>{countMatchingCVs(search)}</strong>
                        </span>
                        <span className="new-count">
                          Naujų: <span className="badge-new">{countNewCVs(search)}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal — card-based UI */}
      {showCreateModal && (
        <div className="modal-overlay prenumeratos-page" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content pren-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSearch ? 'Redaguoti paiešką' : 'Nauja paieška'}</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}><X size={20}/></button>
            </div>

            <div className="pren-modal-body">
              {/* Name */}
              <div className="pren-name-row">
                <div className="form-group">
                  <label>Paieškos pavadinimas *</label>
                  <input type="text" value={modalTitle} onChange={e => setModalTitle(e.target.value)} placeholder="pvz., Programuotojas Vilniuje" />
                </div>
              </div>

              {/* Filter cards */}
              <div className="pren-filter-cards">
                {[
                  { key: 'keywords',       icon: <SearchIcon size={24}/>, label: 'Raktažodžiai / pareigos', chips: formKeywords },
                  { key: 'location',       icon: <MapPin size={24}/>,     label: 'Darbo vietos',            chips: formLocation },
                  { key: 'personal',       icon: <User size={24}/>,       label: 'Asmeniniai duomenys',     chips: [...formGender, ...(formAge ? [`${formAge} m.`] : [])] },
                  { key: 'languages',      icon: <Globe size={24}/>,      label: 'Kalbų įgūdžiai',          chips: formLanguages },
                  { key: 'education',      icon: <GraduationCap size={24}/>, label: 'Išsilavinimas',        chips: formEducation },
                  { key: 'computerSkills', icon: <Monitor size={24}/>,    label: 'Kompiuteriniai įgūdžiai', chips: formComputerSkills },
                  { key: 'driving',        icon: <Car size={24}/>,        label: 'Vairavimo kategorija',    chips: formDrivingLicense },
                  { key: 'workFields',     icon: <Briefcase size={24}/>,  label: 'Darbo patirtis',          chips: formWorkFields },
                  { key: 'salary',         icon: <Euro size={24}/>,       label: 'Atlyginimas nuo',         chips: formSalary ? [`Nuo ${formSalary} €`] : [] },
                  { key: 'workType',       icon: <Clock size={24}/>,      label: 'Darbo tipas',             chips: formWorkType },
                ].map(({ key, icon, label, chips }) => (
                  <div key={key} className="pren-filter-card" onClick={() => setActiveFilterPanel(key)}>
                    <div className="pren-card-header">
                      <span className="pren-card-icon-wrap">
                        {icon}
                        {chips.length > 0 && <span className="pren-filter-badge">{chips.length}</span>}
                      </span>
                      <span className="pren-card-label">{label}</span>
                    </div>
                    {chips.length > 0 ? (
                      <div className="pren-card-chips">
                        {chips.map(v => <span key={v} className="pren-selected-chip">{v}</span>)}
                      </div>
                    ) : (
                      <button type="button" className="pren-card-select-btn"><span>+</span> PASIRINKTI</button>
                    )}
                  </div>
                ))}
              </div>

            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Atšaukti</button>
              <button className="btn-primary" onClick={handleSaveSearch} disabled={!modalTitle.trim() || isSaving}>
                {isSaving ? 'Saugoma...' : editingSearch ? 'Atnaujinti' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RAKTAŽODŽIAI */}
      <FilterModal isOpen={activeFilterPanel === 'keywords'} onClose={() => setActiveFilterPanel(null)} title="Raktažodžiai / pareigos" icon={<SearchIcon size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input type="text" className="filter-input" placeholder="pvz., programuotojas" value={keywordInputVal} onChange={e => setKeywordInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); const v = keywordInputVal.trim(); if (v && !formKeywords.includes(v)) setFormKeywords(p => [...p, v]); setKeywordInputVal(''); }}} />
          <button onClick={() => { const v = keywordInputVal.trim(); if (v && !formKeywords.includes(v)) setFormKeywords(p => [...p, v]); setKeywordInputVal(''); }} style={{ padding: '0 12px', background: '#13B69B', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {formKeywords.map(kw => (
            <span key={kw} style={{ background: '#e8f7f5', color: '#13B69B', border: '1px solid #b2e4dc', borderRadius: 12, padding: '3px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              {kw} <button onClick={() => setFormKeywords(p => p.filter(v => v !== kw))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#13B69B', fontSize: 16, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      </FilterModal>

      {/* DARBO VIETOS */}
      <FilterModal isOpen={activeFilterPanel === 'location'} onClose={() => setActiveFilterPanel(null)} title="Darbo vietos" icon={<MapPin size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <input type="text" className="filter-input" placeholder="Ieškoti miesto..." value={locationSearch} onChange={e => setLocationSearch(e.target.value)} />
        <div className="cities-grid">
          {LITHUANIAN_CITIES.filter(c => !locationSearch || c.toLowerCase().includes(locationSearch.toLowerCase())).map(city => (
            <label key={city} className="city-checkbox-label">
              <input type="checkbox" checked={formLocation.includes(city)} onChange={() => setFormLocation(p => toggleArr(p, city))} />
              <span>{city}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* ASMENINIAI DUOMENYS */}
      <FilterModal isOpen={activeFilterPanel === 'personal'} onClose={() => setActiveFilterPanel(null)} title="Asmeniniai duomenys" icon={<User size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#555' }}>Lytis</h4>
        <div className="checkbox-list single-column">
          {['Vyras', 'Moteris'].map(g => (
            <label key={g} className="checkbox-label">
              <input type="checkbox" checked={formGender.includes(g)} onChange={() => setFormGender(p => toggleArr(p, g))} />
              <span>{g}</span>
            </label>
          ))}
        </div>
        <h4 style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600, color: '#555' }}>Amžius</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min={16} max={65} placeholder="Nuo" value={formAge ? formAge.split('-')[0] : ''} onChange={e => { const max = formAge ? formAge.split('-')[1] || '65' : '65'; setFormAge(e.target.value ? `${e.target.value}-${max}` : ''); }} style={{ width: 80, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
          <span style={{ color: '#999' }}>–</span>
          <input type="number" min={16} max={65} placeholder="Iki" value={formAge ? formAge.split('-')[1] || '' : ''} onChange={e => { const min = formAge ? formAge.split('-')[0] || '16' : '16'; setFormAge(e.target.value ? `${min}-${e.target.value}` : ''); }} style={{ width: 80, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
          <span style={{ color: '#888', fontSize: 13 }}>metai</span>
        </div>
      </FilterModal>

      {/* KALBŲ ĮGŪDŽIAI */}
      <FilterModal isOpen={activeFilterPanel === 'languages'} onClose={() => setActiveFilterPanel(null)} title="Kalbų įgūdžiai" icon={<Globe size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.language || []).map(l => (
            <label key={l.id} className="checkbox-label">
              <input type="checkbox" checked={formLanguages.includes(l.value)} onChange={() => setFormLanguages(p => toggleArr(p, l.value))} />
              <span>{l.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* IŠSILAVINIMAS */}
      <FilterModal isOpen={activeFilterPanel === 'education'} onClose={() => setActiveFilterPanel(null)} title="Išsilavinimas" icon={<GraduationCap size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.education_level || ['Vidurinis','Aukštesnysis','Aukštasis neuniversitetinis','Bakalauras','Magistras','Daktaras'].map((v, i) => ({ id: i, value: v }))).map((l: any) => (
            <label key={l.id} className="checkbox-label">
              <input type="checkbox" checked={formEducation.includes(l.value)} onChange={() => setFormEducation(p => toggleArr(p, l.value))} />
              <span>{l.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* KOMPIUTERINIAI ĮGŪDŽIAI */}
      <FilterModal isOpen={activeFilterPanel === 'computerSkills'} onClose={() => setActiveFilterPanel(null)} title="Kompiuteriniai įgūdžiai" icon={<Monitor size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        {(['computer_skill_office','computer_skill_programming','computer_skill_design','computer_skill_os','computer_skill_accounting','computer_skill_database'] as const).map(cat => {
          const items = cvTemplates[cat] || [];
          if (!items.length) return null;
          const catLabel = cat.replace('computer_skill_','').replace('_',' ');
          return (
            <div key={cat} style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{catLabel}</h4>
              <div className="checkbox-list single-column">
                {items.map(s => (
                  <label key={s.id} className="checkbox-label">
                    <input type="checkbox" checked={formComputerSkills.includes(s.value)} onChange={() => setFormComputerSkills(p => toggleArr(p, s.value))} />
                    <span>{s.value}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </FilterModal>

      {/* VAIRAVIMO KATEGORIJA */}
      <FilterModal isOpen={activeFilterPanel === 'driving'} onClose={() => setActiveFilterPanel(null)} title="Vairavimo kategorija" icon={<Car size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.driving_category || ['A','B','C','D','BE','CE'].map((v,i)=>({id:i,value:v}))).map((c: any) => (
            <label key={c.id} className="checkbox-label">
              <input type="checkbox" checked={formDrivingLicense.includes(c.value)} onChange={() => setFormDrivingLicense(p => toggleArr(p, c.value))} />
              <span>{c.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* DARBO PATIRTIS */}
      <FilterModal isOpen={activeFilterPanel === 'workFields'} onClose={() => setActiveFilterPanel(null)} title="Darbo patirtis" icon={<Briefcase size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list">
          {(cvTemplates.work_field || []).map(f => (
            <label key={f.id} className="checkbox-label">
              <input type="checkbox" checked={formWorkFields.includes(f.value)} onChange={() => setFormWorkFields(p => toggleArr(p, f.value))} />
              <span>{f.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* ATLYGINIMAS */}
      <FilterModal isOpen={activeFilterPanel === 'salary'} onClose={() => setActiveFilterPanel(null)} title="Atlyginimas" icon={<Euro size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="salary-slider-wrap">
          <div className="salary-slider-label">{formSalarySlider === 0 ? 'Bet koks atlyginimas' : `Nuo ${formSalarySlider.toLocaleString('lt-LT')} €`}</div>
          <input type="range" min={0} max={10000} step={50} value={formSalarySlider}
            style={{ '--slider-pct': `${(formSalarySlider/10000)*100}%` } as React.CSSProperties}
            onChange={e => { const v = parseInt(e.target.value,10); setFormSalarySlider(v); setFormSalary(v === 0 ? '' : String(v)); }} />
          <div className="salary-slider-range"><span>0 €</span><span>10 000 €</span></div>
        </div>
      </FilterModal>

      {/* DARBO TIPAS */}
      <FilterModal isOpen={activeFilterPanel === 'workType'} onClose={() => setActiveFilterPanel(null)} title="Darbo tipas" icon={<Clock size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.work_type || ['Nuolatinis','Laikinas','Projektinis','Praktika'].map((v,i)=>({id:i,value:v}))).map((t: any) => (
            <label key={t.id} className="checkbox-label">
              <input type="checkbox" checked={formWorkType.includes(t.value)} onChange={() => setFormWorkType(p => toggleArr(p, t.value))} />
              <span>{t.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>
    </div>
  );
};

export default ManoPaieskos;

